/**
 * The HTTP client all three apps share.
 *
 * Two things worth knowing before using it:
 *
 * 1. **A denial is a 200.** `POST /agent/authorize` returns HTTP 200 for allow, deny AND
 *    escalate — the verdict is in the body. Same for `POST /agent/usage/consume`, where a
 *    refused request is `{allowed: false}` rather than a 4xx. Treating a non-2xx as "the only
 *    failure" is how a console reports a blocked agent as healthy.
 * 2. **The public surface takes no credential.** Every GTM route is callable with no key, on
 *    purpose — that is the whole point of the free tier, and a marketing page holds no secret.
 *    Only the console passes one.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface ClientOptions {
  /** Where the API lives. Same-origin by default — the console is served by it. */
  baseUrl?: string;
  /**
   * A bearer credential. **Server-side callers only.**
   *
   * The console used to pass its `sk_live_` here, read out of `localStorage` on every load —
   * which put a long-lived secret somewhere any script on the origin could read it. It now
   * talks to `/v1/bff/api/*` with `credentials: "same-origin"` and no token at all, and the
   * server attaches the key. Nothing in a browser bundle should set this.
   */
  secretKey?: string;
  /** Sent on every request. For attribution headers the server reads alongside the
   *  credential — never for anything that is itself a credential. */
  headers?: Record<string, string>;
  /**
   * Whether the browser may attach cookies. `"same-origin"` is what the BFF needs.
   *
   * Never `"include"`: the session cookie is `SameSite=Strict` and `__Host-`-prefixed, so it is
   * same-origin by construction, and asking for `include` would only widen what the browser is
   * willing to send.
   */
  credentials?: RequestCredentials;
}

export class Client {
  readonly baseUrl: string;
  private readonly secretKey?: string;
  private readonly extra: Record<string, string>;
  private readonly credentials: RequestCredentials;

  constructor(opts: ClientOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? "").replace(/\/$/, "");
    this.secretKey = opts.secretKey;
    this.extra = opts.headers ?? {};
    // "omit" by default: a public page must not send a cookie it has no business holding, and
    // the browser default ("same-origin") would attach one on the marketing origin too.
    this.credentials = opts.credentials ?? "omit";
  }

  async request<T>(method: Method, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { accept: "application/json", ...this.extra };
    if (body !== undefined) headers["content-type"] = "application/json";
    if (this.secretKey) headers.authorization = `Bearer ${this.secretKey}`;

    const resp = await fetch(this.baseUrl + path, {
      method,
      headers,
      credentials: this.credentials,
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    const text = await resp.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      // A non-JSON body from a JSON route means something upstream answered instead — a proxy
      // error page, usually. Say that, rather than a parse error nobody can act on.
      throw new ApiError(resp.status, "bad_response", `${method} ${path}: not JSON`);
    }

    if (!resp.ok) {
      const err = (parsed as { error?: { code?: string; message?: string } })?.error;
      throw new ApiError(
        resp.status,
        err?.code ?? String(resp.status),
        err?.message ?? `${method} ${path} failed`,
      );
    }
    return parsed as T;
  }

  get = <T>(path: string) => this.request<T>("GET", path);
  post = <T>(path: string, body?: unknown) => this.request<T>("POST", path, body);
  patch = <T>(path: string, body?: unknown) => this.request<T>("PATCH", path, body);
  put = <T>(path: string, body?: unknown) => this.request<T>("PUT", path, body);
  del = <T>(path: string) => this.request<T>("DELETE", path);
}

/** Where the API is, for a page served from somewhere else (the static landing + gtm builds).
 *  Same-origin when unset, which is what the console wants. */
export function apiBase(): string {
  // Written plainly rather than through a cast: `scripts/check_env_example.py` scans for
  // `import.meta.env.VITE_*` to prove every declared front-end variable is actually read,
  // and a cast hides the reference from it. The types come from vite-env.d.ts.
  return (import.meta.env.VITE_AUTHOXI_API ?? "").replace(/\/$/, "");
}
